import {
  pluralRu, countRu, EVENT_FORMS, SECOND_FORMS, MINUTE_FORMS, HOUR_FORMS, DAY_FORMS,
} from './pluralRu';

/**
 * FE-FIX30 (ANALYTICS-OPS-INSIGHTS01). Политика повторяемости правила.
 *
 * Публичный контракт правила V2 — ровно три поля:
 *   recurrence_threshold_count, recurrence_window_value, recurrence_window_unit.
 * Внутреннее имя хранилища `recurrence_count` наружу не выходит: ни в payload,
 * ни в типах, ни в UI (ТЗ §33).
 *
 * Тройка задаётся целиком либо целиком равна null. null означает системную
 * политику backend: порог по умолчанию 3, а окно зависит от контекста вызова,
 * поэтому фронт НЕ подставляет выдуманное окно и не пишет фиктивные значения
 * без действия пользователя.
 */

export const RECURRENCE_DEFAULT = 'default';
export const RECURRENCE_CUSTOM = 'custom';

export const RECURRENCE_UNITS = ['seconds', 'minutes', 'hours', 'days'];

/** Максимальное окно повторяемости: 30 дней (ТЗ №14), в секундах. */
export const RECURRENCE_MAX_WINDOW_SECONDS = 30 * 24 * 60 * 60;

const UNIT_SECONDS = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
};

export const RECURRENCE_UNIT_LABELS = {
  seconds: 'Секунды',
  minutes: 'Минуты',
  hours: 'Часы',
  days: 'Дни',
};

/** Порог системной политики backend. Окно в неё не входит — оно контекстное. */
export const BACKEND_DEFAULT_THRESHOLD = 3;

export const EMPTY_RECURRENCE = {
  mode: RECURRENCE_DEFAULT,
  thresholdCount: '',
  windowValue: '',
  windowUnit: 'days',
};

const isFilled = (value) => value !== null && value !== undefined && value !== '';

/**
 * Состояние формы из ответа backend. Тройка заполнена — режим «Настроить»,
 * иначе «По умолчанию» с пустыми полями (подставлять сюда 3/2/дня нельзя:
 * это превратило бы системную политику в явную пользовательскую).
 */
export const fromRuleRecurrence = (rule) => {
  const count = rule?.recurrence_threshold_count;
  const value = rule?.recurrence_window_value;
  const unit = rule?.recurrence_window_unit;

  if (!isFilled(count) && !isFilled(value) && !isFilled(unit)) return { ...EMPTY_RECURRENCE };

  return {
    mode: RECURRENCE_CUSTOM,
    thresholdCount: isFilled(count) ? String(count) : '',
    windowValue: isFilled(value) ? String(value) : '',
    // Неизвестную единицу не подменяем: данные правила не переписываются молча.
    windowUnit: isFilled(unit) ? String(unit) : 'days',
  };
};

/**
 * Клиентская проверка держит только очевидные границы ТЗ §33 (порог >= 2,
 * окно — положительное целое). Всё остальное — предмет backend-валидации:
 * подменять её «исправлением» значений запрещено.
 */
export const validateRecurrence = (value) => {
  const errors = {};
  if (value?.mode !== RECURRENCE_CUSTOM) return { valid: true, errors };

  const count = Number(value.thresholdCount);
  if (value.thresholdCount === '' || !Number.isInteger(count) || count < 2) {
    errors.thresholdCount = 'Целое число не меньше 2';
  }

  const window = Number(value.windowValue);
  if (value.windowValue === '' || !Number.isInteger(window) || window <= 0) {
    errors.windowValue = 'Целое число больше нуля';
  }

  if (!RECURRENCE_UNITS.includes(value.windowUnit)) {
    errors.windowUnit = 'Выберите единицу окна';
  } else if (!errors.windowValue && window * UNIT_SECONDS[value.windowUnit] > RECURRENCE_MAX_WINDOW_SECONDS) {
    errors.windowValue = 'Окно не больше 30 дней';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Режим «По умолчанию» отправляет явные null: правило могло иметь кастомную
 * политику раньше, и пропуск полей оставил бы её нетронутой.
 */
export const toRecurrenceDto = (value) => {
  if (value?.mode !== RECURRENCE_CUSTOM) {
    return {
      recurrence_threshold_count: null,
      recurrence_window_value: null,
      recurrence_window_unit: null,
    };
  }

  return {
    recurrence_threshold_count: Number(value.thresholdCount),
    recurrence_window_value: Number(value.windowValue),
    recurrence_window_unit: value.windowUnit,
  };
};

const plural = pluralRu;

const COMPACT_UNIT_FORMS = {
  seconds: SECOND_FORMS,
  minutes: MINUTE_FORMS,
  hours: HOUR_FORMS,
  days: DAY_FORMS,
};

/** Винительный падеж для «за N …»: меняется только форма единственного числа. */
const accusative = (forms, one) => [one, forms[1], forms[2]];

const UNIT_FORMS = {
  seconds: accusative(SECOND_FORMS, 'секунду'),
  minutes: accusative(MINUTE_FORMS, 'минуту'),
  hours: HOUR_FORMS,
  days: DAY_FORMS,
};

/** Краткие подписи единиц окна для строк со сводкой. */
export const RECURRENCE_UNIT_SHORT = {
  seconds: 'с',
  minutes: 'мин',
  hours: 'ч',
  days: 'дн',
};

export const formatRecurrenceWindow = (value, unit) => {
  const forms = UNIT_FORMS[unit];
  if (!isFilled(value) || !forms) return '';
  const count = Number(value);
  if (!Number.isFinite(count)) return '';
  return `${count} ${plural(count, forms)}`;
};

/**
 * Подпись для Rule Detail (ТЗ §33). Для системной политики показываем только
 * то, что реально известно: порог 3; окно определяет backend по контексту.
 */
export const describeRecurrence = (rule) => {
  const count = rule?.recurrence_threshold_count;
  const value = rule?.recurrence_window_value;
  const unit = rule?.recurrence_window_unit;

  if (!isFilled(count) || !isFilled(value) || !isFilled(unit)) {
    return `Порог ${BACKEND_DEFAULT_THRESHOLD} ${plural(BACKEND_DEFAULT_THRESHOLD, EVENT_FORMS)}, окно подбирается автоматически`;
  }

  const window = formatRecurrenceWindow(value, unit);
  if (!window) return 'Особое окно повторяемости';
  const n = Number(count);
  return `${n} ${plural(n, EVENT_FORMS)} за ${window}`;
};

/**
 * Компактная подпись для ячейки таблицы: «По умолчанию», «3/30 секунд»,
 * «5/6 дней». Неизвестная единица показывается нейтрально.
 */
export const formatRecurrenceCompact = (rule) => {
  const count = rule?.recurrence_threshold_count;
  const value = rule?.recurrence_window_value;
  const unit = rule?.recurrence_window_unit;

  if (!isFilled(count) || !isFilled(value) || !isFilled(unit)) return 'По умолчанию';

  const forms = COMPACT_UNIT_FORMS[unit];
  if (!forms) return 'Особое окно';
  return `${Number(count)}/${countRu(Number(value), forms)}`;
};
